import React, { useState, useCallback } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import { DEFAULT_SAMS_ACCESS_CONFIG, type SamsAccessConfig } from './constants';

const SLUG = 'area-access-config';

interface AreaAccessConfigData {
  samsAccessConfig?: SamsAccessConfig;
  dbSamsAccessConfig?: SamsAccessConfig;
  updatingParams?: boolean;
}

export default function AreaAccessConfig(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<AreaAccessConfigData>(props);
  const [showExpanded, setShowExpanded] = useState(false);
  const pageUpdatingPStar = pageData?.data?.updatingPStar as boolean | undefined;

  const formValue = formContext?.values?.[SLUG] as SamsAccessConfig | undefined;
  const injectedConfig = data.samsAccessConfig ?? data.dbSamsAccessConfig ?? DEFAULT_SAMS_ACCESS_CONFIG;
  const samsAccessConfig = formValue ?? injectedConfig ?? DEFAULT_SAMS_ACCESS_CONFIG;
  const updatingPStar = (data.updatingParams ?? pageUpdatingPStar) ?? false;

  const updateConfig = useCallback(
    (newConfig: SamsAccessConfig) => {
      formContext?.setValue(SLUG, newConfig);
    },
    [formContext]
  );

  const handleModeChange = useCallback(
    (area: string, mode: string) => {
      const newConfig = { ...samsAccessConfig };
      newConfig[area] = {
        mode: mode as 'das_based' | 'trip_based' | 'closed',
        ...(mode === 'trip_based'
          ? {
              trips_per_vessel: newConfig[area]?.trips_per_vessel ?? 1,
              possession_limit_lbs: newConfig[area]?.possession_limit_lbs ?? 12000,
            }
          : {}),
      };
      updateConfig(newConfig);
    },
    [samsAccessConfig, updateConfig]
  );

  const handleTripsChange = useCallback(
    (area: string, value: number) => {
      const newConfig = { ...samsAccessConfig };
      newConfig[area] = {
        ...newConfig[area],
        trips_per_vessel: value || 1,
      };
      updateConfig(newConfig);
    },
    [samsAccessConfig, updateConfig]
  );

  const handleLimitChange = useCallback(
    (area: string, value: number) => {
      const newConfig = { ...samsAccessConfig };
      newConfig[area] = {
        ...newConfig[area],
        possession_limit_lbs: value || 12000,
      };
      updateConfig(newConfig);
    },
    [samsAccessConfig, updateConfig]
  );

  return (
    <div className="noscroll-value-control" style={{ marginTop: '16px' }}>
      <div
        onClick={() => setShowExpanded(!showExpanded)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <h3>
          <b>Area Access Configuration</b>
        </h3>
        <span style={{ fontSize: '20px' }}>{showExpanded ? '▼' : '▶'}</span>
      </div>

      {showExpanded && (
        <div
          style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px',
          }}
        >
          <table
            style={{
              width: '100%',
              fontSize: '11px',
              tableLayout: 'fixed',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                <th style={{ textAlign: 'left', padding: '4px 2px', width: '25%' }}>Area</th>
                <th style={{ textAlign: 'center', padding: '4px 2px', width: '35%' }}>Mode</th>
                <th style={{ textAlign: 'center', padding: '4px 2px', width: '20%' }}>Trips</th>
                <th style={{ textAlign: 'center', padding: '4px 2px', width: '20%' }}>Limit</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(samsAccessConfig).map((area) => (
                <tr key={area} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <td style={{ padding: '4px 2px', fontWeight: '500', fontSize: '10px' }}>{area}</td>
                  <td style={{ padding: '4px 2px', textAlign: 'center' }}>
                    <select
                      value={samsAccessConfig[area]?.mode ?? 'das_based'}
                      onChange={(e) => handleModeChange(area, e.target.value)}
                      disabled={updatingPStar}
                      style={{
                        padding: '2px 4px',
                        borderRadius: '4px',
                        border: '1px solid rgba(255,255,255,0.3)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'inherit',
                        fontSize: '10px',
                        width: '70px',
                        minWidth: '70px',
                        maxWidth: '70px',
                      }}
                    >
                      <option value="das_based">DAS</option>
                      <option value="trip_based">Trip</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                  <td style={{ padding: '4px 2px', textAlign: 'center' }}>
                    {samsAccessConfig[area]?.mode === 'trip_based' ? (
                      <input
                        type="number"
                        min={0}
                        max={10}
                        value={samsAccessConfig[area]?.trips_per_vessel ?? 1}
                        onChange={(e) =>
                          handleTripsChange(area, parseInt(e.target.value, 10) || 1)
                        }
                        disabled={updatingPStar}
                        style={{
                          width: '100%',
                          padding: '2px',
                          borderRadius: '4px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'inherit',
                          textAlign: 'center',
                          fontSize: '10px',
                        }}
                      />
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '4px 2px', textAlign: 'center' }}>
                    {samsAccessConfig[area]?.mode === 'trip_based' ? (
                      <input
                        type="number"
                        min={0}
                        max={50000}
                        step={1000}
                        value={samsAccessConfig[area]?.possession_limit_lbs ?? 12000}
                        onChange={(e) =>
                          handleLimitChange(area, parseInt(e.target.value, 10) || 12000)
                        }
                        disabled={updatingPStar}
                        style={{
                          width: '100%',
                          padding: '2px',
                          borderRadius: '4px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'inherit',
                          textAlign: 'center',
                          fontSize: '10px',
                        }}
                      />
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              marginTop: '8px',
              fontSize: '10px',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            <strong>Modes:</strong> DAS = days-at-sea, Trip = rotational access, Closed = no fishing
          </div>
        </div>
      )}
    </div>
  );
}
